import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import type { ModalProps } from '@/types';

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = 'max-w-2xl',
  headerVariant = 'default',
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center"
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className={`relative z-[10000] mx-4 max-h-[90vh] w-full overflow-y-auto rounded-lg bg-card shadow-lg ${maxWidth}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className={
            headerVariant === 'drawer'
              ? 'flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-6 py-5'
              : 'flex items-center justify-between border-b border-border p-6'
          }
        >
          <h2
            id="modal-title"
            className={
              headerVariant === 'drawer'
                ? 'text-base font-semibold text-gray-900'
                : 'text-xl font-semibold text-card-foreground'
            }
          >
            {title}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size={headerVariant === 'drawer' ? 'icon' : 'sm'}
            onClick={onClose}
            className={
              headerVariant === 'drawer'
                ? 'h-7 w-7 shrink-0 rounded-md border border-gray-200 text-gray-500 hover:border-[#4369a0]/30 hover:bg-[#4369a0]/10 hover:text-[#4369a0]'
                : undefined
            }
          >
            <X className={headerVariant === 'drawer' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
            {headerVariant === 'drawer' && <span className="sr-only">Close</span>}
          </Button>
        </div>

        <div
          className={
            headerVariant === 'drawer'
              ? 'px-6 py-5 text-card-foreground'
              : 'p-6 text-card-foreground'
          }
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
